import {
  FocusableComponentLayout,
  FocusContext,
  FocusDetails,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { resetVideo as resetVideoDispatch } from 'common/actions';
import Carousel from 'common/components/carousel/Carousel';
import StripeView, { StripeViewProps } from 'common/components/stripe/StripeView';
import variables from 'common/config/variables';
import { IViewWithDetailsStripeItem } from 'common/constants/data-types';
import { AppTheme, DetailInfoStripeItem, Nullable, StripeItemData } from 'common/interfaces';
import { getClassName } from 'common/utils';
import { usePinPopup } from 'common/utils/hooks/usePinPopup';
import { AppDispatch, StoreState } from 'index';
import { debounce, DebouncedFunc, isEmpty } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import DetailedInfo from './DetailedInfo';
import './ViewWithDetails.styles';
const { DETAILED_INFO_THROTTLE_TIMEOUT, THROTTLE_TIMEOUT } = variables;

interface StripeHeaderRef {
  focus?: () => void;
}

// eslint-disable-next-line react/display-name
const StripeHeader = React.forwardRef<StripeHeaderRef, React.PropsWithChildren>(
  (props, customRef) => {
    const { ref, focusKey, focusSelf } = useFocusable();

    useImperativeHandle(
      customRef,
      () => ({
        focus: () => focusSelf()
      }),
      []
    );

    return (
      <FocusContext.Provider value={focusKey}>
        <div ref={ref} className="view-details-stripe-header-container">
          {props.children}
        </div>
      </FocusContext.Provider>
    );
  }
);

export interface ViewWithDetailsRefObject {
  currentAsset: Nullable<DetailInfoStripeItem>;
  setDetailedInfoAsset: (asset: Nullable<DetailInfoStripeItem>) => void;
}

interface ViewWithDetailsProps extends Omit<StripeViewProps, 'isPlayerFullscreen'> {
  customBannerView?: React.ReactElement;
  hideBanners?: boolean;
  disableBannerExpanding?: boolean;
  disableBanners?: boolean;
  getSelectedAssetData?: (asset: IViewWithDetailsStripeItem) => Nullable<DetailInfoStripeItem>;
  stripeHeader?: React.ReactNode;
  preferredChildFocusKey?: string;
  header?: React.ReactNode;
  loader?: React.ReactNode;
}
const ViewWithDetails = React.forwardRef<ViewWithDetailsRefObject, ViewWithDetailsProps>(
  function ViewWithDetails(props: ViewWithDetailsProps, customRef) {
    const stripeViewFocusedRef = useRef(false);
    const [stripeViewFocused, setStripeViewFocused] = useState(false);
    const [lockedFocusToContainer, setLockFocusToContainer] = useState(false);
    const { pathname } = useLocation();
    const focusKeyValue = useMemo(() => props.focusKey || pathname, [pathname]);
    const focusKeyStripeView = useMemo(() => `${focusKeyValue}-stripe-view`, [focusKeyValue]);

    const _onFocus = useCallback(() => {
      // If the returned focus is outside the stripe view handle the reset
      if (!stripeViewFocusedRef.current) {
        handleResetVideo(undefined);
      }
    }, []);
    const dispatch = useDispatch<AppDispatch>();
    const carouselSelected = useSelector(
      (state: StoreState) => state.carousel.isExpanded,
      shallowEqual
    );
    const isPlayerOpenned = useSelector(
      (state: StoreState) => state.videoPlayer.isOpen && state.videoPlayer.video.isFullscreen,
      shallowEqual
    );
    const navigateMode = useSelector(
      (state: StoreState) => state.appConfig.navigateMode,
      shallowEqual
    );
    const { ref, hasFocusedChild, focusKey } = useFocusable({
      isFocusBoundary: lockedFocusToContainer,
      focusKey: focusKeyValue,
      saveLastFocusedChild: true,
      trackChildren: true,
      forceFocus: true,
      onFocus: _onFocus
    });
    const [detailInfoVisible, setDetailInfoVisible] = useState<boolean>(false);
    const [detailInfoLoading, setDetailInfoLoading] = useState<boolean>(false);
    const [currentAsset, setDetailedInfoAsset] = useState<Nullable<DetailInfoStripeItem>>(null);
    const currentAssetRef = useRef<Nullable<DetailInfoStripeItem>>(currentAsset);
    const idleCallbackIdRef = useRef(-1);
    const isPlayerOpennedRef = useRef(isPlayerOpenned);
    const stripeHeaderRef = useRef<StripeHeaderRef>(null);
    const updateDetailedInfoThrottleRef = useRef<DebouncedFunc<VoidFunction> | null>(null);
    const { checkOpened } = usePinPopup();

    const resetVideo = useCallback(() => {
      return dispatch(resetVideoDispatch());
    }, [dispatch]);

    useImperativeHandle(
      customRef,
      () => ({
        currentAsset,
        setDetailedInfoAsset
      }),
      [currentAsset]
    );

    const detailedInfoActive = useMemo(
      () => detailInfoLoading && (navigateMode === 'DIRECTIONAL' ? stripeViewFocused : true),
      [navigateMode, stripeViewFocused, detailInfoLoading]
    );

    useEffect(() => {
      currentAssetRef.current = currentAsset;
    }, [currentAsset]);

    useEffect(() => {
      let keyDown = false;
      const onKeyUp = () => {
        if (keyDown) {
          setLockFocusToContainer(false);
        }
        keyDown = false;
      };
      const onKeyDown = () => {
        if (keyDown) {
          setLockFocusToContainer(true);
        }
        keyDown = true;
      };
      // Handle listener for keypress to avoid going to the sidemenu when holding keydown
      if (hasFocusedChild && variables.WHITELABEL_TYPE === AppTheme.LINEAR) {
        document.addEventListener('keyup', onKeyUp);
        document.addEventListener('keydown', onKeyDown);
      }

      return () => {
        document.removeEventListener('keyup', onKeyUp);
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [hasFocusedChild]);

    useEffect(() => {
      isPlayerOpennedRef.current = isPlayerOpenned;
    }, [isPlayerOpenned]);

    const cleanUp = () => {
      cancelAnimationFrame(idleCallbackIdRef.current);
      updateDetailedInfoThrottleRef.current?.cancel();
      setDetailInfoVisible(false);
      variables.WHITELABEL_TYPE !== AppTheme.LINEAR && resetVideo();
    };

    // Clean up
    useEffect(() => {
      return () => {
        cleanUp();
      };
    }, []);

    const parseAsset = useCallback(
      (asset: StripeItemData<IViewWithDetailsStripeItem>) => {
        return props.getSelectedAssetData && asset && props.getSelectedAssetData(asset);
      },
      [props.getSelectedAssetData]
    );

    const handleResetVideo = useCallback((newAsset: Nullable<DetailInfoStripeItem>) => {
      // Skip reset based on the current app theme
      if (variables.WHITELABEL_TYPE !== AppTheme.ON_DEMAND) return;
      //
      // Poll data to prevent rerendering
      // Reset last video when switching a show
      // Compare by value deep
      JSON.stringify(newAsset) !== JSON.stringify(currentAssetRef.current) && resetVideo();
    }, []);

    const getUpdateAssetDebounce = useCallback(() => {
      // Don't throttle when removing or showing detailed info
      // Poll current asset for performance boost
      if (isEmpty(currentAssetRef.current)) {
        return 0;
      }
      return navigateMode === 'DIRECTIONAL' ? DETAILED_INFO_THROTTLE_TIMEOUT : THROTTLE_TIMEOUT;
    }, [navigateMode]);

    const handleSelectedAsset = useCallback(
      (asset: Nullable<DetailInfoStripeItem>) => {
        // Don't throttle when removing or showing detailed info
        updateDetailedInfoThrottleRef.current?.cancel();
        cancelAnimationFrame(idleCallbackIdRef.current);
        updateDetailedInfoThrottleRef.current = debounce(() => {
          idleCallbackIdRef.current = requestAnimationFrame(() => {
            asset && setDetailedInfoAsset(asset);
            setDetailInfoLoading(!!asset);
            setDetailInfoVisible(!!asset);
          });
        }, getUpdateAssetDebounce());
        updateDetailedInfoThrottleRef.current();
      },
      [getUpdateAssetDebounce]
    );

    const onStripeItemFocus = useCallback(
      (asset: StripeItemData<IViewWithDetailsStripeItem>) => {
        props.onStripeItemFocus && props.onStripeItemFocus(asset);
        const newAsset = parseAsset(asset);
        handleSelectedAsset(newAsset);
        handleResetVideo(newAsset);
      },
      [props.onStripeItemFocus, handleSelectedAsset, parseAsset]
    );

    const onStripeItemBlur = useCallback(() => {
      props.onStripeItemBlur && props.onStripeItemBlur();
      setDetailInfoLoading(false);
    }, [props.onStripeItemBlur]);

    const onStripeItemHover = useCallback(
      (asset: StripeItemData<IViewWithDetailsStripeItem>) => {
        props.onStripeItemHover && props.onStripeItemHover(asset);
        const newAsset = parseAsset(asset);
        handleSelectedAsset(newAsset);
        handleResetVideo(newAsset);
      },
      [props.onStripeItemHover, handleSelectedAsset, parseAsset]
    );

    const onBlur = useCallback(
      (layout?: FocusableComponentLayout, data?: object, details?: FocusDetails) => {
        // Don't call on blur on pointer navigation when hideBanners is active to avoid jumping
        if (navigateMode === 'POINTER' && props.hideBanners) return;
        props.onBlur && layout && details && props.onBlur(layout, data || {}, details);
        //
        cancelAnimationFrame(idleCallbackIdRef.current);
        setDetailInfoLoading(false);
        stripeViewFocusedRef.current = false;
        setStripeViewFocused(false);

        // Don't clear asset when opening player with popups
        const openedPopup = checkOpened();

        if (!isPlayerOpennedRef.current && !openedPopup) {
          cleanUp();
        }
      },
      [props.onBlur, navigateMode, props.hideBanners]
    );

    const onFocus = useCallback(
      (layout: FocusableComponentLayout, data: object, details: FocusDetails) => {
        props.onFocus && props.onFocus(layout, data, details);
        stripeViewFocusedRef.current = true;
        setStripeViewFocused(true);
      },
      [props.onFocus]
    );

    const onBack = useCallback(
      (last?: boolean) => {
        // If the stripe view is focused, blur it
        if (props.stripeHeader && last) {
          stripeHeaderRef.current?.focus?.();
          return false;
        }
        const backStatus = props.onBack?.(last);
        return props.onBack ? !!backStatus : true;
      },
      [props.stripeHeader]
    );

    return (
      <FocusContext.Provider value={focusKey}>
        <div
          ref={ref}
          className={getClassName('view-with-detail-wrapper', {
            'missing-detailed-info': props.hideBanners && !currentAsset
          })}
        >
          {!isEmpty(props.header) && (
            <div className="view-details-header-container">{props.header}</div>
          )}
          {currentAsset && (
            <DetailedInfo
              key={focusKey}
              epg={currentAsset.epg}
              isLiveCard={currentAsset.isLiveCard}
              resolution={currentAsset.resolution}
              active={detailedInfoActive}
              hidden={!detailInfoVisible}
              thumbnailUrl={currentAsset.thumbnailUrl}
              hasHeader={!isEmpty(props.header)}
            />
          )}
          {!props.hideBanners && (
            <Carousel
              disabled={props.disableBanners}
              visible={!detailInfoVisible}
              customView={props.customBannerView}
              disableExpanding={props.disableBannerExpanding}
            />
          )}
          <div
            className={getClassName('stripe-view-container', {
              'carousel-expanded': !carouselSelected,
              'disabled-banners': props.hideBanners,
              'detail-info-visible': detailInfoVisible
            })}
            onMouseLeave={onBlur as any}
          >
            {props.stripeHeader && (
              <StripeHeader ref={stripeHeaderRef}>{props.stripeHeader}</StripeHeader>
            )}
            <StripeView
              focusKey={focusKeyStripeView}
              loading={props.loading}
              loader={props.loader}
              emptyState={props.emptyState}
              onStripeItemFocus={onStripeItemFocus}
              onStripeItemHover={onStripeItemHover}
              onStripeItemBlur={onStripeItemBlur}
              onStripeItemPress={props.onStripeItemPress}
              onFocus={onFocus}
              onBlur={onBlur}
              onBack={onBack}
              axis={props.axis}
              align={props.align}
              disabled={props.disabled}
              stripeAlign={props.stripeAlign}
              initiallyFocused={props.initiallyFocused}
              initialIndex={props.initialIndex}
              stripeScrollPadding={props.stripeScrollPadding}
              stripeItemClassName={props.stripeItemClassName}
              saveFocusOnUnmount={props.saveFocusOnUnmount}
              disableVirtual={props.disableVirtual}
            >
              {props.children}
            </StripeView>
          </div>
        </div>
      </FocusContext.Provider>
    );
  }
);

export default React.memo(ViewWithDetails);
